// Components that instantiate on instructions from a piece of YAML
import {
    Kws,
    chainOrSuspend,
    pyCall,
    pyCallOrSuspend,
    pyDict,
    pyException,
    pyFunc,
    pyNone,
    pyObject,
    pyStr,
    pySuper,
    pyTrue,
    suspensionToPromise as toPromise,
    toPy,
    tryCatchOrSuspend,
} from "@Sk";
import { BASE_MODULES } from "@designer/python-environment";
import { Component, ComponentConstructor } from "@runtime/components/Component";
import {
    SetupResult,
    YamlCreationStack,
    addFormComponentsToLayout,
    instantiateComponentFromYamlSpec,
    mkInvalidComponent,
    setupFormComponents,
} from "@runtime/runner/component-creation";
import type { FormYaml } from "@runtime/runner/data";
import {
    FORM_HIDE_EVENT,
    FORM_REFRESH_DATA_BINDINGS_EVENT,
    FORM_SHOW_EVENT,
    ITEM_PROPERTY,
    setupPassiveHooks,
} from "@runtime/runner/forms";
import { getFormClassObject, getFormInstantiator, resolveFormSpec } from "@runtime/runner/instantiation";
import {
    anvilMod,
    jsObjToKws,
    kwsToObj,
    reportError,
    s_add_component,
    s_layout,
    s_setattr,
    s_slots,
    strError,
} from "@runtime/runner/py-util";
import { Slot, WithLayout } from "@runtime/runner/python-objects";
import { appConfig, appIndex, getFormYaml } from "./app-content";
import { SlotPlaceholder } from "./components/SlotPlaceholder";
import { editState, setupActiveYamlDomElement, setupSections } from "./edit-state";
import { COMPONENT_OVERRIDES, getAnvilComponentInstantiator } from "./instantiation";

export interface YamlCarrierState {
    formName: string;
    depId: string | null;
    active: boolean;
    withError: boolean;
    pyLayout?: Component;
    pyLayoutSlots?: pyDict<pyStr, Slot>;
}

// For passive YAML carriers, we need dummy subclasses of the containers or WithLayout
const propertyCarrierSubclasses = new Map<ComponentConstructor, ComponentConstructor>();

interface PropertyCarrierState {
    values: { [name: string]: pyObject };
}

const getPropertyCarrierSubclass = (cls: ComponentConstructor) => {
    const existingSubclass = propertyCarrierSubclasses.get(cls);
    if (existingSubclass) {
        return existingSubclass;
    }
    const subclass = Sk.misceval.buildClass(
        BASE_MODULES["anvil"],
        ($gbl, $loc) => {
            $loc["__setattr__"] = new pyFunc((self: pyObject, attr: pyStr, val: pyObject) => {
                const props = self._PropertyCarrierState as PropertyCarrierState | undefined;
                const attrStr = attr.toString();
                if (props && attrStr in props?.values) {
                    props.values[attrStr] = val;
                    return pyNone;
                } else {
                    return new pySuper(subclass, self).tp$getattr(s_setattr)!.tp$call([attr, val]);
                }
            });
            $loc["__getattribute__"] = new pyFunc((self: pyObject, attr: pyStr) => {
                const props = self._PropertyCarrierState as PropertyCarrierState | undefined;
                const attrStr = attr.toString();
                if (props && attrStr in props.values) {
                    return props.values[attrStr];
                } else {
                    return new pySuper(subclass, self).tp$getattr(attr, true);
                }
            });
        },
        "YamlCarrierFor" + cls.prototype.tp$name,
        [cls]
    ) as ComponentConstructor;

    propertyCarrierSubclasses.set(cls, subclass);
    return subclass;
};

function setupPassiveYamlCarrier(
    yaml: FormYaml,
    yamlCarrierData: YamlCarrierState,
    pyCarrier: Component,
    domNode: HTMLElement,
    setupResult: SetupResult,
    kws: Kws
) {
    setupPassiveHooks(yaml, pyCarrier);
}

const FORM_TEMPLATE_EVENTS = ["refreshing_data_bindings"];
const WITH_LAYOUT_EVENTS = ["show", "hide", ...FORM_TEMPLATE_EVENTS];

async function setupActiveYamlCarrier(
    yaml: FormYaml,
    yamlCarrierData: YamlCarrierState,
    pyCarrier: Component,
    domNode: HTMLElement,
    setupResult: SetupResult
) {
    const isContainer = !!yaml.container;
    const topLevelComponent = isContainer ? pyCarrier : yamlCarrierData.pyLayout!;
    editState.componentsByName.set("", topLevelComponent);
    editState.namesByComponent.set(topLevelComponent, "");
    editState.ancestorsByComponent.set(topLevelComponent, []);
    setupActiveYamlDomElement(topLevelComponent, "");
    setupSections("", topLevelComponent);
    let oldEvents = topLevelComponent.anvil$hooks.events;
    if (!Array.isArray(oldEvents)) {
        oldEvents = Object.values(oldEvents); // Backwards compat for now.
    }
    //console.log("setupActiveYamlCarrier: ", topLevelComponent.toString(), " old events =", oldEvents);
    const inheritedEvents = isContainer ? FORM_TEMPLATE_EVENTS : WITH_LAYOUT_EVENTS;
    const newEvents = oldEvents.filter((event) => !inheritedEvents.includes(event.name));
    if (isContainer) {
        for (let i = 0; i < newEvents.length; i++) {
            // override show and hide with better descriptions
            // but only if supported by the container
            const event = newEvents[i];
            if (event.name === "show") {
                newEvents[i] = FORM_SHOW_EVENT;
            } else if (event.name === "hide") {
                newEvents[i] = FORM_HIDE_EVENT;
            }
        }
    } else {
        // merge the layouts exposed events with WithLayout inherited events
        newEvents.push(FORM_SHOW_EVENT);
        newEvents.push(FORM_HIDE_EVENT);
    }
    newEvents.push(FORM_REFRESH_DATA_BINDINGS_EVENT);

    topLevelComponent.anvil$hooks.events = newEvents;

    const properties = [...topLevelComponent.anvil$hooks.properties];
    if (properties.every(({ name }) => name !== "item")) {
        properties.push(ITEM_PROPERTY);
    }
    pyCarrier.anvil$hooks.properties = properties;

    editState.orphanedComponents = setupResult.orphanedComponents;

    // Look through dict of components in this carrier, link their pyComponents to their dom nodes and add them to componentsByName
    for (const [name, { component, layoutProperties, index, ancestors, targetSlot }] of Object.entries(
        setupResult.components
    )) {
        component.anvil$hooks.updateDesignName?.(name);
        setupActiveYamlDomElement(component, name);
        editState.componentsByName.set(name, component);
        editState.namesByComponent.set(component, name);
        editState.ancestorsByComponent.set(component, ancestors);
        editState.layoutPropertyInfoByComponent.set(component, { layoutProperties, index, targetSlot });
        setupSections(name, component);
    }
    // If this form's *layout* has slots, fill their caches and populate them with template components
    for (const [pyName, pySlot] of yamlCarrierData.pyLayoutSlots?.$items() || []) {
        const { _slotState } = pySlot;
        const templateSpec = _slotState.templateSpec;
        try {
            await Sk.misceval.asyncToPromise(() => _slotState.fillCache());
        } catch (e: any) {
            reportError(new pyException(`Could not initialise slot '${pyName.toString()}: ${e.toString()}`));
            continue;
        }
        if (templateSpec && _slotState.canAddComponent()) {
            const kws: Kws = [];
            for (const [propName, jsV] of Object.entries(templateSpec.component.properties || {})) {
                kws.push(propName, toPy(jsV));
            }
            let pyTemplate: Component;
            try {
                const fullName = templateSpec.component.type;
                let instantiator: any;
                if (fullName.startsWith("anvil.")) {
                    instantiator = getAnvilComponentInstantiator(
                        { requestingComponent: pyCarrier },
                        fullName.substring(6)
                    );
                } else {
                    // This must be a form.
                    const [_, appPackage, formName] = fullName.match(/^([^.]+)\.(.*)$/) || [null, null, null];
                    if (!formName) {
                        throw new Error(`Must specify an app package for form '${fullName}'`);
                    }
                    if (appPackage === (appConfig.package_name || "main_app_package")) {
                        instantiator = getFormInstantiator(
                            { requestingComponent: yamlCarrierData.pyLayout! },
                            new pyStr(formName)
                        );
                    } else {
                        const depId = Object.keys(appIndex.dependencies).find(
                            (depId) => appIndex.dependencies[depId].packageName === appPackage
                        );
                        if (depId) {
                            instantiator = getFormInstantiator(
                                { requestingComponent: yamlCarrierData.pyLayout! },
                                toPy(`${depId}:${formName}`)
                            );
                        } else {
                            throw new Error(`No such dependency package: ${appPackage}`);
                        }
                    }
                }

                pyTemplate = await toPromise(() => chainOrSuspend(instantiator, (instantiate) => instantiate(kws)));
            } catch (e) {
                reportError(e as pyException);
                pyTemplate = mkInvalidComponent(strError(e));
            }
            await toPromise(() => pyCallOrSuspend(pySlot.tp$getattr(s_add_component), [pyTemplate]));
            const element = await toPromise(() => pyTemplate.anvil$hooks.setupDom());
            element.setAttribute("data-anvil-selectable", "true");
            editState.templatesByDomNode.set(element, {
                targetType: "slot",
                slotName: pyName.toString(),
                slot: pySlot,
                template: templateSpec,
            });
            _slotState.templateComponent = pyTemplate;
        }
    }

    // If this form has its own slots (ie it can *be* a layout), fill them with SlotPlaceholders.
    for (const [name, pySlot] of Object.entries(setupResult.slots || {})) {
        const { template: templateYaml } = yaml.slots![name];
        const template: Component | undefined =
            templateYaml &&
            (await toPromise(() =>
                tryCatchOrSuspend(
                    () =>
                        instantiateComponentFromYamlSpec(
                            { requestingComponent: pyCarrier, fromYaml: true, defaultDepId: null },
                            templateYaml.type,
                            {
                                __ignore_property_exceptions: pyTrue,
                                ...templateYaml.properties,
                            },
                            undefined
                        ),
                    (exception) => {
                        reportError(exception);
                        console.error(
                            `Error instantiating template for slot ${name}`,
                            ": ",
                            exception,
                            "\n",
                            strError(exception)
                        );
                        return undefined;
                    }
                )
            ));
        if (template) {
            template.anvil$hooks.updateDesignName?.(`Slot: ${name}`);
        }

        const placeholder = pyCall<SlotPlaceholder>(SlotPlaceholder, [new pyStr(name), pySlot]);
        try {
            try {
                await toPromise(() => pyCallOrSuspend(pySlot.tp$getattr(s_add_component), [template || placeholder]));
            } catch (e: any) {
                reportError(e);
                editState.orphanedSlots.push(name);
            }
            const element = (template || placeholder).anvil$hooks.domElement!;
            element.setAttribute("data-anvil-selectable", "true");
            const slotInfo = { slot: pySlot, name, placeholder, template, element };
            editState.slotsByName.set(name, slotInfo);
            editState.slotsByDomNode.set(element, slotInfo);
            editState.slotsByComponent.set(template || placeholder, slotInfo);
        } catch (e: any) {
            const err = new pyException(
                `Error adding ${template ? "template" : "placeholder"} to slot '${name}': ${e.toString()}`
            );
            reportError(err);
        }
    }
}

const constructComponent = (
    yamlSpec: string,
    defaultDepId: string | null,
    props: Kws,
    usePropertyCarrierSubclass?: boolean
) =>
    chainOrSuspend(
        yamlSpec.startsWith("form:")
            ? getFormClassObject(resolveFormSpec(yamlSpec.substring(5), defaultDepId))
            : COMPONENT_OVERRIDES[yamlSpec] || anvilMod[yamlSpec],
        (constructor) =>
            pyCallOrSuspend(
                usePropertyCarrierSubclass ? getPropertyCarrierSubclass(constructor!) : constructor!,
                [],
                props
            )
    );

async function doCreateYAMLCarrier(
    formName: string,
    depId: string | null,
    yamlStack: YamlCreationStack,
    { withError, active, kws }: Required<YamlOptions>
) {
    const yaml: FormYaml = await getFormYaml(depId, formName);
    if (!yaml) {
        throw new Sk.builtin.ImportError(`No such form ${formName}`);
    }
    const yamlCarrierData: YamlCarrierState = { formName, depId, withError, active };
    let setupResult: SetupResult;
    if (yaml.container) {
        setupResult = await toPromise(() =>
            chainOrSuspend(
                constructComponent(yaml.container!.type, depId, jsObjToKws(yaml.container!.properties || {}), !active),
                (pyCarrier: Component) => setupFormComponents(yaml, pyCarrier, depId, yamlStack, false, pyCarrier)
            )
        );
        if (!active) {
            const kwObj = kwsToObj(kws);
            setupResult.form._PropertyCarrierState = {
                values: Object.fromEntries(
                    (yaml.properties ?? []).map(({ name, default_value }) => [
                        name,
                        kwObj[name] ?? toPy(default_value ?? null),
                    ])
                ),
            };
        }
    } else {
        const pyCarrier = Sk.misceval.callsimArray(WithLayout, []);

        await toPromise(() =>
            chainOrSuspend(
                constructComponent(yaml.layout!.type, depId, jsObjToKws(yaml.layout!.properties || {})),
                (pyLayout) =>
                    chainOrSuspend(
                        setupFormComponents(yaml, pyCarrier, depId, yamlStack, false, pyLayout),
                        (result) => {
                            setupResult = result;
                            yamlCarrierData.pyLayout = pyLayout;
                            yamlCarrierData.pyLayoutSlots = pyLayout.tp$getattr(s_slots);
                            Sk.abstr.sattr(pyCarrier, s_layout, pyLayout);
                            return chainOrSuspend(
                                addFormComponentsToLayout(yaml, pyCarrier, pyLayout),
                                (orphanedComponents: string[]) => {
                                    setupResult.orphanedComponents.push(...orphanedComponents);
                                }
                            );
                        }
                    )
            )
        );
    }

    const pyCarrier = setupResult!.form;
    pyCarrier.anvil$yamlCarrier = yamlCarrierData;

    let domNode;
    try {
        domNode = await toPromise(() => setupResult.form.anvil$hooks.setupDom());
    } catch (e: any) {
        reportError(new pyException(`Could not initialise layout: ${e.toString()}`));
        return pyCarrier; // We couldn't build the form, everything is broken, bail out.
    }

    if (withError) {
        domNode.style.border = "2px solid red";
    }

    if (active) {
        await setupActiveYamlCarrier(yaml, yamlCarrierData, pyCarrier, domNode, setupResult!);
    } else {
        setupPassiveYamlCarrier(yaml, yamlCarrierData, pyCarrier, domNode, setupResult!, kws);
    }
    editState.yamlCarrierByDomNode.set(domNode, pyCarrier);
    domNode.setAttribute("data-anvil-yaml-carrier", "true");

    return pyCarrier;
}

interface YamlOptions {
    withError?: boolean;
    active?: boolean;
    kws?: Kws;
}

export const createYamlCarrier = (
    formName: string,
    depId: string | null,
    yamlStack: YamlCreationStack,
    { withError = false, active = false, kws = [] }: YamlOptions
) => Sk.misceval.promiseToSuspension(doCreateYAMLCarrier(formName, depId, yamlStack, { withError, active, kws }));
